import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  VerifiedOutlined as SignedIcon,
  DownloadOutlined as DownloadIcon,
  CancelOutlined as VoidIcon,
  CheckCircle,
} from '@mui/icons-material';
import { brand, text as textColors } from '../../theme/colors';
import DialogCloseButton from '../common/DialogCloseButton';
import DigitalSignatureCapture from '../signatures/DigitalSignatureCapture';
import signatureService from '../../services/signatureService';
import contractService from '../../services/contractService';
import { sectionsFromBody } from '../../utils/contractSections';

/**
 * ContractViewer
 *
 * Renders a contract (parties, version, status, body sections) and offers the
 * right action for the viewer:
 *  - client + 'sent'            -> Review & sign (DigitalSignatureCapture)
 *  - any party + 'executed'     -> Download PDF
 *  - contractor + draft|sent    -> Void
 *
 * Props: { open, onClose, contract, bid, user, onChanged }
 */
const STATUS_COLORS = {
  draft: 'default',
  sent: 'info',
  client_signed: 'warning',
  executed: 'success',
  void: 'default',
};

const statusLabel = (status) => {
  const label = (status || 'draft').replaceAll('_', ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const CONTRACT_CONSENT_TEXT = `By signing this contract electronically, I agree that my electronic signature is the legal equivalent of my manual signature. I consent to be legally bound by this contract's terms and conditions.

This electronic signature is governed by the Electronic Transactions Act [Chapter 22:05] of Zimbabwe, the ESIGN Act, and UETA. The signature is binding and enforceable.

I acknowledge that I have reviewed the complete contract, agree to all of its terms, am authorized to sign it, and that my electronic signature has the same legal effect as a handwritten signature.`;

const ContractViewer = ({ open, onClose, contract: contractProp, bid, user, onChanged }) => {
  const [contract, setContract] = useState(contractProp || null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    setContract(contractProp || null);
  }, [contractProp]);

  const showMessage = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  if (!contract) return null;

  const isClient = String(user?.id) === String(contract.client_id);
  const isContractor = String(user?.id) === String(contract.contractor_id);
  const status = contract.status;

  const canSign = isClient && status === 'sent';
  const canDownload = (isClient || isContractor) && (status === 'executed' || status === 'sent');
  const canVoid = isContractor && (status === 'draft' || status === 'sent');

  const sections = sectionsFromBody(contract.body);

  const signerName =
    `${user?.profile?.firstName || user?.first_name || ''} ${user?.profile?.lastName || user?.last_name || ''}`.trim() ||
    user?.company_name ||
    user?.email ||
    'Client';

  const reloadContract = async () => {
    try {
      const fresh = await contractService.getById(contract.id);
      if (fresh) setContract(fresh);
    } catch (error) {
      console.error('Reload contract failed:', error);
    }
  };

  // Client signs: create the signature, then execute the contract.
  const handleSignatureComplete = async (signaturePayload) => {
    setLoading(true);
    try {
      const signatureResponse = await signatureService.createSignature(signaturePayload);
      const signatureId = signatureResponse?.signature?.id;
      await contractService.sign(contract.id, signatureId);
      setSignatureModalOpen(false);
      showMessage('Contract executed.', 'success');
      await reloadContract();
      if (onChanged) onChanged();
    } catch (error) {
      console.error('Sign contract failed:', error);
      setSignatureModalOpen(false);
      showMessage(error.response?.data?.error || error.message || 'Failed to sign the contract.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const url = await contractService.fetchPdfObjectUrl(contract.id);
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Download contract PDF failed:', error);
      showMessage(error.response?.data?.error || 'Failed to open the contract PDF.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleVoid = async () => {
    setLoading(true);
    try {
      await contractService.voidContract(contract.id);
      setVoidConfirmOpen(false);
      showMessage('Contract voided.', 'success');
      await reloadContract();
      if (onChanged) onChanged();
    } catch (error) {
      console.error('Void contract failed:', error);
      setVoidConfirmOpen(false);
      showMessage(error.response?.data?.error || error.message || 'Failed to void the contract.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const contractorName = bid?.contractor_name || contract.contractor_name || 'Contractor';
  const clientName = bid?.client_name || contract.client_name || 'Client';
  const projectName = bid?.project_name || contract.project_name || 'Project';

  return (
    <>
      <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => !loading && onClose()} />
        <DialogTitle sx={{ background: brand.gradient, color: 'white', pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SignedIcon />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Contract
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {projectName}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {/* Header meta */}
          <Paper
            elevation={0}
            sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Version {contract.version}
              </Typography>
              <Chip label={statusLabel(status)} color={STATUS_COLORS[status] || 'default'} size="small" sx={{ fontWeight: 600 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Contractor</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{contractorName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Client</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{clientName}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {status === 'void' && (
            <Alert severity="info" sx={{ mb: 2 }}>This contract has been voided and is no longer in effect.</Alert>
          )}
          {status === 'executed' && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle fontSize="inherit" />}>
              Contract executed — signed by both parties.
            </Alert>
          )}
          {canSign && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This contract is awaiting your signature. Review each clause below, then sign to execute it.
            </Alert>
          )}

          {/* Body sections */}
          {sections.length === 0 ? (
            <Typography variant="body2" color="text.secondary">This contract has no content.</Typography>
          ) : (
            sections.map((section) => (
              <Box key={section.key} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: textColors.primary }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColors.secondary }}>
                  {section.text || '—'}
                </Typography>
              </Box>
            ))
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Standard legal clauses version: {contract.clauses_version || 'n/a'}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider' }}>
          {canVoid && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<VoidIcon />}
              onClick={() => setVoidConfirmOpen(true)}
              disabled={loading}
            >
              Void
            </Button>
          )}
          {canDownload && (
            <Button
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              Download PDF
            </Button>
          )}
          {canSign && (
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SignedIcon />}
              onClick={() => setSignatureModalOpen(true)}
              disabled={loading}
              sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
            >
              Review &amp; sign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Client signature capture */}
      <DigitalSignatureCapture
        open={signatureModalOpen}
        onClose={() => !loading && setSignatureModalOpen(false)}
        onSignatureComplete={handleSignatureComplete}
        documentType="contract"
        documentId={contract.id}
        documentData={contract.body}
        signerName={signerName}
        signerEmail={user?.email || ''}
        consentText={CONTRACT_CONSENT_TEXT}
      />

      {/* Void confirmation */}
      <Dialog open={voidConfirmOpen} onClose={() => !loading && setVoidConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Void contract?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voiding cancels this contract. {status === 'sent' ? 'The client will no longer be able to sign it.' : ''} This cannot be undone — you would need to create a new contract.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidConfirmOpen(false)} disabled={loading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleVoid} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <VoidIcon />}>
            Void contract
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContractViewer;
